/**
 * Lore — Obsidian-vault-backed campaign codex.
 *
 * Read-path slice. Three panels:
 *   - Left: folder tree of every note the caller can see, grouped by the
 *     `folder` returned in the manifest. Selecting a leaf loads the note.
 *   - Centre: rendered markdown. Wiki-links and image embeds are pre-resolved
 *     by the API; we replace `[[target]]` with either an internal link or a
 *     ghosted span before handing the markdown to `marked`.
 *   - Right: a frontmatter sidebar (visibility, reveal date, summary, tags).
 *     For DMs this also exposes the persona switcher — picking a player here
 *     re-fetches the manifest with `?as={folder}` so the DM can preview the
 *     view exactly as that player sees it.
 *
 * Critically, **ghost links are visually identical** for "doesn't exist" and
 * "exists but caller can't see it". This is enforced server-side too, but
 * the client must also avoid leaking via cursor/title/data attributes.
 *
 * The page is intentionally read-only in this slice. The DM admin panel
 * (visibility checkboxes, reveal picker, draft toggle, section gating
 * toolbar) lives in a follow-up commit alongside the patch endpoint.
 */
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type MouseEvent as ReactMouseEvent,
} from 'react';
import { marked } from 'marked';
import {
  fetchImageBlob,
  getManifest,
  getNote,
  listMyNotes,
  LoreManifestEntry,
  LoreNote,
  MyNoteEntry,
} from '../lib/loreApi';
import { useAuth } from '../lib/AuthContext';
import { isAdmin } from '../lib/auth';
import { useIdentity } from '../lib/ImpersonationContext';
import LoreEditor from './LoreEditor';
import MyNoteEditor from './MyNoteEditor';
import FolderVisibilityModal from './FolderVisibilityModal';
import './FolderVisibilityModal.css';
import './Lore.css';

/**
 * Folder names that the persona heuristic should NOT treat as player folders
 * when populating the preview-as dropdown. Anything not in this set and at
 * the top level of the vault is assumed to be a player and offered up. The
 * server still validates against players.json so getting this wrong is at
 * worst a confusing dropdown entry.
 */
const LoreSharedFolders = new Set<string>([
  'Lore',
  'NPCs',
  'Locations',
  'Items',
  'Sessions',
  'dnd-scribe',
  'ChatGPT Import',
  'Dixity',
  'Templates',
  'Maps',
]);

interface TreeFolder {
  name: string;
  fullPath: string;
  notes: LoreManifestEntry[];
  children: TreeFolder[];
}

/**
 * Builds a hierarchical tree from a flat manifest. The manifest's `folder`
 * field is a slash-separated path; we walk each entry into the tree, creating
 * intermediate folders as we go. Folders are sorted alphabetically; notes
 * within a folder are sorted by title.
 */
function buildTree(entries: LoreManifestEntry[]): TreeFolder {
  const root: TreeFolder = { name: '', fullPath: '', notes: [], children: [] };

  for (const entry of entries) {
    const segments = entry.folder ? entry.folder.split('/').filter(Boolean) : [];
    let cursor = root;
    let acc = '';
    for (const seg of segments) {
      acc = acc ? `${acc}/${seg}` : seg;
      let child = cursor.children.find((c) => c.name === seg);
      if (!child) {
        child = { name: seg, fullPath: acc, notes: [], children: [] };
        cursor.children.push(child);
      }
      cursor = child;
    }
    cursor.notes.push(entry);
  }

  const sortRecursive = (node: TreeFolder) => {
    node.children.sort((a, b) => a.name.localeCompare(b.name));
    node.notes.sort((a, b) => a.title.localeCompare(b.title));
    node.children.forEach(sortRecursive);
  };
  sortRecursive(root);
  return root;
}

function FolderNode({
  folder,
  selectedSlug,
  onPick,
  onFolderContextMenu,
  depth = 0,
}: {
  folder: TreeFolder;
  selectedSlug: string | null;
  onPick: (entry: LoreManifestEntry) => void;
  onFolderContextMenu?: (e: ReactMouseEvent, folderPath: string) => void;
  depth?: number;
}) {
  // Auto-expand the root and any folder that contains the selected note.
  const [open, setOpen] = useState(depth === 0);

  return (
    <div className="lore-tree__folder" style={{ paddingLeft: depth === 0 ? 0 : '0.75rem' }}>
      {folder.name && (
        <button
          type="button"
          className="lore-tree__folder-label"
          onClick={() => setOpen((o) => !o)}
          onContextMenu={(e) => {
            if (onFolderContextMenu) {
              e.preventDefault();
              onFolderContextMenu(e, folder.fullPath);
            }
          }}
        >
          <span className="lore-tree__caret">{open ? '▾' : '▸'}</span>
          {folder.name}
        </button>
      )}
      {(open || depth === 0) && (
        <>
          {folder.children.map((child) => (
            <FolderNode
              key={child.fullPath}
              folder={child}
              selectedSlug={selectedSlug}
              onPick={onPick}
              onFolderContextMenu={onFolderContextMenu}
              depth={depth + 1}
            />
          ))}
          <ul className="lore-tree__notes">
            {folder.notes.map((note) => (
              <li
                key={note.slug}
                className={
                  'lore-tree__note' +
                  (note.slug === selectedSlug ? ' lore-tree__note--selected' : '') +
                  (note.type === 'canvas' ? ' lore-tree__note--canvas' : '')
                }
              >
                <button type="button" onClick={() => onPick(note)}>
                  {note.title}
                </button>
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}

/**
 * Substitutes `[[target]]`, `[[target|alias]]`, and `![[image]]` with HTML
 * placeholder tags BEFORE marked sees them. Resolution data comes from the
 * server (`note.links`, `note.images`) — the client never has to know about
 * Obsidian's resolution rules.
 *
 *   visible link  → `<a class="lore-link" data-lore-slug="...">display</a>`
 *   ghost link    → `<span class="lore-ghost">display</span>`
 *   visible image → `<img class="lore-image" data-lore-image-slug="..."
 *                        alt="..." src="">` (src is patched in by the
 *                    image-blob effect after marked runs)
 *   ghost image   → `<span class="lore-image-placeholder">📷 …</span>`
 *
 * Images are emitted with an empty `src` because <img src> can't carry the
 * Authorization header. A separate effect fetches each `data-lore-image-slug`
 * with the bearer token, builds an object URL, and patches the `src` in.
 *
 * Note that we look up by RAW target text from the API's links/images arrays,
 * which is the exact string the API saw inside the brackets. Two embeds with
 * the same target share the same lookup result.
 */
function preprocessWikiLinks(
  content: string,
  links: LoreNote['links'],
  images: LoreNote['images'],
): string {
  // Build maps for O(1) lookup.
  const linkMap = new Map<string, (typeof links)[number]>();
  for (const link of links) {
    if (!linkMap.has(link.rawTarget)) linkMap.set(link.rawTarget, link);
  }
  const imageMap = new Map<string, (typeof images)[number]>();
  for (const img of images) {
    if (!imageMap.has(img.rawTarget)) imageMap.set(img.rawTarget, img);
  }

  // Image embeds first (they need the leading `!`).
  let out = content.replace(/!\[\[([^\]]+)\]\]/g, (_, inner: string) => {
    const rawTarget = inner.trim();
    const [target, alias] = inner.split('|').map((s) => s.trim());
    const display = alias || target;
    // Look up by full raw target first (so `foo.png|alt` and `foo.png` are
    // distinct entries on the server side), then fall back to the bare target
    // for the common alias-free case.
    const ref = imageMap.get(rawTarget) ?? imageMap.get(target);
    if (ref && ref.visible && ref.slug) {
      return (
        `<img class="lore-image" data-lore-image-slug="${escapeHtml(ref.slug)}"` +
        ` alt="${escapeHtml(display)}" src="" />`
      );
    }
    return `<span class="lore-image-placeholder">📷 ${escapeHtml(display)}</span>`;
  });

  out = out.replace(/\[\[([^\]]+)\]\]/g, (_, inner: string) => {
    const [target, alias] = inner.split('|').map((s) => s.trim());
    const display = alias || target;
    const link = linkMap.get(target);
    if (link && link.visible && link.resolvedSlug) {
      // Use a custom data attribute so the click handler can intercept and
      // route locally instead of doing a hard navigation.
      return `<a class="lore-link" data-lore-slug="${escapeHtml(link.resolvedSlug)}">${escapeHtml(display)}</a>`;
    }
    // Ghost: identical render whether the target doesn't exist or the caller
    // can't see it. Cursor stays default; no title attribute.
    return `<span class="lore-ghost">${escapeHtml(display)}</span>`;
  });

  return out;
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

export default function Lore() {
  const { user } = useAuth();
  const { impersonating, target } = useIdentity();
  // When impersonating a player, suppress DM UI — the DM sees the page
  // exactly as the impersonated player would.
  const dm = !impersonating && isAdmin(user);

  const [manifest, setManifest] = useState<LoreManifestEntry[] | null>(null);
  const [manifestError, setManifestError] = useState<string | null>(null);
  const [manifestWarning, setManifestWarning] = useState<string | null>(null);
  // Authoritative player folder names from players.json, returned by the
  // manifest endpoint (DM-only). Used for the editor's visibility checkboxes
  // so they show real players instead of heuristic-guessed folder names.
  const [knownPlayers, setKnownPlayers] = useState<string[]>([]);
  const [selectedSlug, setSelectedSlug] = useState<string | null>(null);
  const [note, setNote] = useState<LoreNote | null>(null);
  const [noteLoading, setNoteLoading] = useState(false);
  const [noteError, setNoteError] = useState<string | null>(null);

  // Persona is the DM's preview-as-player choice. Empty string = real DM view.
  // For non-DM users this stays empty and the controls are hidden.
  const [persona, setPersona] = useState<string>('');

  // Effective `as` parameter for all lore API calls. When the global
  // impersonation bar is active, its Cognito sub takes precedence over the
  // lore-specific persona dropdown (which is hidden during impersonation
  // anyway). The server accepts both folder names and Cognito subs in `?as=`.
  const effectiveAs = impersonating && target ? target.sub : persona || undefined;

  // Edit mode is the DM-only side panel toggle. The editor only mounts when
  // editMode is on AND the caller is the DM AND there's a selected slug;
  // switching personas (preview-as) auto-disables edit mode below.
  const [editMode, setEditMode] = useState(false);

  // Player notes — caller-scoped list from /lore/my-notes. The DM sees their
  // own folder's notes here too (via the same endpoint, scoped server-side
  // by the existing players.json mapping). Selecting a my-note swaps the
  // centre panel into the player editor instead of the lore read-view.
  const [myNotes, setMyNotes] = useState<MyNoteEntry[]>([]);
  const [myNotesError, setMyNotesError] = useState<string | null>(null);
  const [selectedMyNoteSlug, setSelectedMyNoteSlug] = useState<string | null>(null);
  const [newMyNote, setNewMyNote] = useState<{ slug: string } | null>(null);

  // Context menu + bulk-visibility modal state (DM only).
  const [ctxMenu, setCtxMenu] = useState<{ x: number; y: number; folder: string } | null>(null);
  const [bulkVisFolder, setBulkVisFolder] = useState<string | null>(null);

  // Dismiss the context menu on any click outside it.
  useEffect(() => {
    if (!ctxMenu) return;
    const dismiss = () => setCtxMenu(null);
    window.addEventListener('click', dismiss);
    return () => window.removeEventListener('click', dismiss);
  }, [ctxMenu]);

  const handleFolderContextMenu = useCallback(
    (e: ReactMouseEvent, folderPath: string) => {
      if (!dm) return;
      setCtxMenu({ x: e.clientX, y: e.clientY, folder: folderPath });
    },
    [dm],
  );

  const loadMyNotes = useCallback(async () => {
    setMyNotesError(null);
    try {
      const result = await listMyNotes();
      setMyNotes(result.entries);
    } catch (e) {
      setMyNotesError(e instanceof Error ? e.message : String(e));
    }
  }, []);

  useEffect(() => {
    void loadMyNotes();
  }, [loadMyNotes]);

  const loadManifest = useCallback(
    async (refresh = false) => {
      setManifestError(null);
      setManifestWarning(null);
      try {
        const result = await getManifest({
          refresh,
          as: effectiveAs,
        });
        setManifest(result.entries);
        setManifestWarning(result.warning ?? null);
        // Capture the authoritative player list from players.json (DM-only).
        // Only update when we get a real list — persona-switched requests
        // won't include it, so we keep the last known value.
        if (result.players) setKnownPlayers(result.players);
        // Stamp the entry count so the Dashboard's "new since last visit"
        // hint resets when the player actually visits the Lore page.
        try {
          localStorage.setItem(
            'goobdoob:lore:lastSeenCount',
            String(result.entries.length),
          );
        } catch { /* quota / private mode */ }
      } catch (e) {
        setManifestError(e instanceof Error ? e.message : String(e));
      }
    },
    [effectiveAs],
  );

  // Reload the manifest whenever the persona/impersonation changes (or on
  // first mount). Clear the selected note so the user doesn't sit on content
  // that may not be visible to the new persona.
  useEffect(() => {
    setSelectedSlug(null);
    setNote(null);
    void loadManifest(false);
  }, [loadManifest]);

  // Load the selected note. Re-fires when persona changes too — the same slug
  // may have different visible content under a different persona.
  useEffect(() => {
    if (!selectedSlug) {
      setNote(null);
      return;
    }
    let cancelled = false;
    setNoteLoading(true);
    setNoteError(null);
    getNote(selectedSlug, { as: effectiveAs })
      .then((result) => {
        if (!cancelled) {
          setNote(result);
        }
      })
      .catch((e) => {
        if (!cancelled) {
          setNoteError(e instanceof Error ? e.message : String(e));
          setNote(null);
        }
      })
      .finally(() => {
        if (!cancelled) setNoteLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedSlug, effectiveAs]);

  const tree = useMemo(() => (manifest ? buildTree(manifest) : null), [manifest]);

  // The set of unique persona folders the DM can preview as. Built from the
  // manifest under real-DM view; we cache it so switching persona doesn't
  // empty the dropdown. (Players never see this control.)
  const [knownPersonas, setKnownPersonas] = useState<string[]>([]);
  useEffect(() => {
    if (!dm || persona !== '') return; // only learn personas from the real-DM manifest
    if (!manifest) return;
    const folders = new Set<string>();
    for (const entry of manifest) {
      const top = entry.folder.split('/')[0];
      // Heuristic: a folder is a player persona if it isn't one of the known
      // shared/lore folders. The DM still picks from the dropdown, so the
      // worst-case here is showing an extra option that the server will then
      // ignore (it validates against players.json).
      if (top && !LoreSharedFolders.has(top)) folders.add(top);
    }
    setKnownPersonas([...folders].sort());
  }, [dm, persona, manifest]);

  const handlePickNote = useCallback((entry: LoreManifestEntry) => {
    setSelectedMyNoteSlug(null);
    setNewMyNote(null);
    if (entry.type === 'canvas') {
      // Canvas (.canvas) files are character sheets — open in /sheet eventually.
      // For now we still set the slug so the centre panel shows the placeholder.
      setSelectedSlug(entry.slug);
      return;
    }
    setSelectedSlug(entry.slug);
  }, []);

  // Intercept clicks on rendered <a class="lore-link"> tags inside the note
  // body so they navigate within this page instead of doing a hard reload.
  const handleBodyClick = useCallback(
    (e: ReactMouseEvent<HTMLDivElement>) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest('a.lore-link') as HTMLAnchorElement | null;
      if (!anchor) return;
      const slug = anchor.getAttribute('data-lore-slug');
      if (!slug) return;
      e.preventDefault();
      setSelectedSlug(slug);
    },
    [],
  );

  const renderedHtml = useMemo(() => {
    if (!note || note.type === 'canvas') return '';
    const preprocessed = preprocessWikiLinks(note.content, note.links, note.images);
    // marked v14 returns string|Promise<string>; we use the sync default.
    return marked.parse(preprocessed, { async: false }) as string;
  }, [note]);

  // Image-blob effect: after each render of a note body, find every
  // <img class="lore-image" data-lore-image-slug="..."> placeholder, fetch
  // the corresponding image with the bearer token, build an object URL, and
  // patch it into the src. Object URLs are cached per slug for the lifetime
  // of the page so re-renders (e.g. switching back to a previously-viewed
  // note) reuse the same blob without a re-fetch.
  //
  // Cleanup revokes only the URLs created by this hook so we don't leak —
  // the cache is keyed on slug, so the same image embedded in two notes
  // shares one blob URL.
  const bodyRef = useRef<HTMLElement | null>(null);
  const imageCache = useRef<Map<string, string>>(new Map());
  useEffect(() => {
    const root = bodyRef.current;
    if (!root) return;
    const placeholders = root.querySelectorAll<HTMLImageElement>(
      'img.lore-image[data-lore-image-slug]',
    );
    if (placeholders.length === 0) return;

    let cancelled = false;
    const cache = imageCache.current;
    placeholders.forEach((img) => {
      const slug = img.getAttribute('data-lore-image-slug');
      if (!slug) return;
      // Already patched in a previous run? Skip.
      if (img.getAttribute('src')) return;
      const cached = cache.get(slug);
      if (cached) {
        img.src = cached;
        return;
      }
      void fetchImageBlob(slug, effectiveAs ? { as: effectiveAs } : undefined)
        .then((url) => {
          if (cancelled) {
            URL.revokeObjectURL(url);
            return;
          }
          cache.set(slug, url);
          img.src = url;
        })
        .catch(() => {
          // 404/403/network — leave the empty placeholder. The CSS makes it
          // render as a quiet broken-image box rather than a giant gap.
          img.classList.add('lore-image--missing');
        });
    });

    return () => {
      cancelled = true;
    };
  }, [renderedHtml, effectiveAs]);

  // Revoke every cached blob URL when the page unmounts. We do NOT revoke on
  // every render because the same slug may be reused across notes.
  useEffect(() => {
    const cache = imageCache.current;
    return () => {
      for (const url of cache.values()) URL.revokeObjectURL(url);
      cache.clear();
    };
  }, []);

  // When the DM is in edit mode, the grid collapses to two columns:
  // left sidebar + full-width editor (which contains its own split pane).
  const inEditMode = dm && editMode && persona === '';

  return (
    <div className={`lore-page${inEditMode ? ' lore-page--edit' : ''}`}>
      <aside className="lore-sidebar lore-sidebar--left">
        <header className="lore-sidebar__header">
          <h2>Lore</h2>
          <button
            type="button"
            className="lore-refresh"
            title="Refresh manifest"
            onClick={() => void loadManifest(true)}
          >
            ↻
          </button>
        </header>
        {manifestError && <div className="lore-error">{manifestError}</div>}
        {dm && manifestWarning && (
          <div className="lore-error" style={{ background: 'var(--color-warning, #4a3800)', borderColor: 'var(--color-amber, #d4a017)' }}>
            ⚠ players.json: {manifestWarning}
          </div>
        )}
        {!manifest && !manifestError && (
          <div className="lore-empty">Loading…</div>
        )}
        {tree && tree.children.length === 0 && tree.notes.length === 0 && (
          <div className="lore-empty">
            Nothing visible to you yet. Ask the DM to share a note.
          </div>
        )}
        {tree && (
          <FolderNode
            folder={tree}
            selectedSlug={selectedSlug}
            onPick={handlePickNote}
            onFolderContextMenu={dm ? handleFolderContextMenu : undefined}
          />
        )}

        <section className="lore-tree__my-notes">
          <header className="lore-tree__my-notes-header">
            <h3>My notes</h3>
            <button
              type="button"
              title="Create a new note"
              onClick={() => {
                const raw = window.prompt(
                  'New note path (e.g. "Combats/Wally vs goblins.md"):',
                  '',
                );
                if (!raw) return;
                let trimmed = raw.trim();
                if (!trimmed) return;
                if (!trimmed.toLowerCase().endsWith('.md')) trimmed += '.md';
                if (trimmed.startsWith('/') || trimmed.includes('..')) {
                  window.alert('Invalid path.');
                  return;
                }
                // Per-segment URL-encode for the slug. The server will reverse
                // this and validate the literal vault path.
                const slug = trimmed
                  .split('/')
                  .map((s) => encodeURIComponent(s))
                  .join('/');
                setNewMyNote({ slug });
                setSelectedMyNoteSlug(slug);
                setSelectedSlug(null);
              }}
            >
              + New
            </button>
          </header>
          {myNotesError && <div className="lore-error">{myNotesError}</div>}
          {!myNotesError && myNotes.length === 0 && !newMyNote && (
            <div className="lore-empty">No personal notes yet.</div>
          )}
          <ul className="lore-tree__notes">
            {myNotes.map((n) => (
              <li
                key={n.slug}
                className={
                  'lore-tree__note' +
                  (n.slug === selectedMyNoteSlug ? ' lore-tree__note--selected' : '')
                }
              >
                <button
                  type="button"
                  onClick={() => {
                    setNewMyNote(null);
                    setSelectedMyNoteSlug(n.slug);
                    setSelectedSlug(null);
                  }}
                  title={n.slug}
                >
                  {decodeURIComponent(n.slug)}
                </button>
              </li>
            ))}
          </ul>
        </section>
      </aside>

      {/* ── Centre + Right: either read-view or full-width editor ── */}
      {inEditMode ? (
        /* Editor takes over centre + right columns as a single cell */
        <LoreEditor
          slug={selectedSlug}
          knownPlayers={knownPlayers}
          onClose={() => setEditMode(false)}
          onPublished={() => {
            void loadManifest(true);
            if (selectedSlug) {
              const slug = selectedSlug;
              setSelectedSlug(null);
              window.setTimeout(() => setSelectedSlug(slug), 0);
            }
          }}
        />
      ) : (
        <>
          <main className="lore-main">
            {!selectedSlug && !selectedMyNoteSlug && (
              <div className="lore-empty lore-empty--centre">
                Pick a note from the tree on the left.
              </div>
            )}
            {selectedMyNoteSlug && (
              <MyNoteEditor
                key={selectedMyNoteSlug + (newMyNote ? ':new' : '')}
                slug={selectedMyNoteSlug}
                isNew={newMyNote?.slug === selectedMyNoteSlug}
                onCreated={() => {
                  setNewMyNote(null);
                  void loadMyNotes();
                }}
                onDeleted={() => {
                  const dropped = selectedMyNoteSlug;
                  setSelectedMyNoteSlug(null);
                  setNewMyNote(null);
                  if (dropped) void loadMyNotes();
                }}
              />
            )}
            {selectedSlug && noteLoading && <div className="lore-empty">Loading note…</div>}
            {selectedSlug && noteError && <div className="lore-error">{noteError}</div>}
            {selectedSlug && note && note.type === 'canvas' && (
              <div className="lore-canvas-placeholder">
                <h3>{selectedSlug}</h3>
                <p>This is a character sheet. Open it in the game view.</p>
              </div>
            )}
            {selectedSlug && note && note.type === 'markdown' && (
              <article
                ref={bodyRef}
                className="lore-body"
                onClick={handleBodyClick}
                dangerouslySetInnerHTML={{ __html: renderedHtml }}
              />
            )}
          </main>

          <aside className="lore-sidebar lore-sidebar--right">
            {dm && persona === '' && (
              <section className="lore-meta lore-meta--editmode">
                <button
                  type="button"
                  className="lore-edit-toggle"
                  onClick={() => setEditMode(true)}
                  disabled={!selectedSlug}
                  title={
                    selectedSlug
                      ? 'Open the editor for this note'
                      : 'Select a note first'
                  }
                >
                  ✎ Edit this note
                </button>
              </section>
            )}
            {dm && (
              <section className="lore-meta lore-meta--persona">
                <h3>Preview as</h3>
                <select
                  value={persona}
                  onChange={(e) => {
                    setPersona(e.target.value);
                    setSelectedSlug(null);
                    if (e.target.value !== '') setEditMode(false);
                  }}
                >
                  <option value="">DM (everything)</option>
                  {knownPersonas.map((p) => (
                    <option key={p} value={p}>
                      {p}
                    </option>
                  ))}
                </select>
                <p className="lore-meta__hint">
                  Persona switches manifest + note fetches to <code>?as={'{folder}'}</code>.
                  Server validates against players.json — unknown folders fall back to
                  real DM view.
                </p>
              </section>
            )}
            {note && note.type === 'markdown' && (
              <section className="lore-meta">
                <h3>About this note</h3>
                {note.frontmatter.summary && (
                  <p className="lore-meta__summary">{note.frontmatter.summary}</p>
                )}
                <dl className="lore-meta__list">
                  <dt>Visibility</dt>
                  <dd>{describePlayers(note.frontmatter.players)}</dd>
                  {note.frontmatter.reveal && (
                    <>
                      <dt>Reveal date</dt>
                      <dd>{note.frontmatter.reveal}</dd>
                    </>
                  )}
                  {note.frontmatter.tags && note.frontmatter.tags.length > 0 && (
                    <>
                      <dt>Tags</dt>
                      <dd>
                        {note.frontmatter.tags.map((t) => (
                          <span key={t} className="lore-tag">
                            {t}
                          </span>
                        ))}
                      </dd>
                    </>
                  )}
                  <dt>ETag</dt>
                  <dd className="num lore-meta__etag">{note.etag}</dd>
                </dl>
              </section>
            )}
          </aside>
        </>
      )}

      {/* ── Right-click context menu for folder visibility (DM only) ── */}
      {ctxMenu && (
        <div
          className="lore-ctx"
          style={{ left: ctxMenu.x, top: ctxMenu.y }}
          onClick={(e) => e.stopPropagation()}
        >
          <button
            type="button"
            className="lore-ctx__item"
            onClick={() => {
              setBulkVisFolder(ctxMenu.folder);
              setCtxMenu(null);
            }}
          >
            Set visibility…
          </button>
        </div>
      )}

      {/* ── Bulk folder visibility modal (DM only) ── */}
      {bulkVisFolder && (
        <FolderVisibilityModal
          folder={bulkVisFolder}
          knownPlayers={knownPlayers}
          onApplied={() => {
            void loadManifest(true);
          }}
          onClose={() => setBulkVisFolder(null)}
        />
      )}
    </div>
  );
}

function describePlayers(p: LoreNote['frontmatter']['players']): string {
  if (!p) return 'DM only (default)';
  if (p.all) return 'All players';
  if (p.explicitNone) return 'DM only (explicit)';
  if (p.allowed && p.allowed.length > 0) return p.allowed.join(', ');
  return 'DM only (default)';
}
